import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { track } from '@/lib/analytics';
import { FiArrowLeft, FiCheckCircle, FiAlertCircle, FiXCircle, FiSlash } from 'react-icons/fi';
import { Button, Card } from '@/components';
import { ExchangeStatusBadge } from '@/components/ExchangeStatusBadge';
import { ApiError } from '@/lib/api';
import {
  createExchangeLog,
  deleteExchangeLog,
  getExchangeLogs,
  updateExchangeLog,
  type ExchangeStatus,
  type ExchangeType,
  type LateReason,
  type ExchangeLog,
} from '@/lib/exchange-api';

const STATUS_OPTIONS: {
  value: ExchangeStatus;
  label: string;
  Icon: typeof FiCheckCircle;
  color: string;
}[] = [
  { value: 'ON_TIME', label: 'On Time', Icon: FiCheckCircle, color: '#059669' },
  { value: 'LATE', label: 'Late', Icon: FiAlertCircle, color: '#D97706' },
  { value: 'NO_SHOW', label: 'No Show', Icon: FiXCircle, color: '#DC2626' },
  { value: 'CANCELLED', label: 'Cancelled', Icon: FiSlash, color: '#6B7280' },
];

const LATE_REASONS: { value: LateReason; label: string }[] = [
  { value: 'TRAFFIC', label: 'Traffic' },
  { value: 'WORK', label: 'Work' },
  { value: 'WEATHER', label: 'Weather' },
  { value: 'EMERGENCY', label: 'Emergency' },
  { value: 'OTHER', label: 'Other' },
];

function formatDisplayDate(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTimeDisplay(timeStr: string | null | undefined): string {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

function LogExchangePage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const params = Route.useSearch();

  useEffect(() => {
    track('Log Exchange Screen');
  }, []);

  const [status, setStatus] = useState<ExchangeStatus | null>(null);
  const [minutesLate, setMinutesLate] = useState('');
  const [lateReason, setLateReason] = useState<LateReason | null>(null);
  const [note, setNote] = useState('');
  const [existingLog, setExistingLog] = useState<ExchangeLog | null>(null);
  const [error, setError] = useState('');

  const existingQuery = useQuery({
    queryKey: [
      'exchange-log-existing',
      params.beneficiary_id,
      params.scheduled_date,
      params.exchange_type,
    ],
    queryFn: async () => {
      const result = await getExchangeLogs(getToken, {
        start_date: params.scheduled_date,
        end_date: params.scheduled_date,
        beneficiary_id: Number(params.beneficiary_id),
      });
      const match = result.exchange_logs.find(
        (log) => log.exchange_type === params.exchange_type
      );
      return match ?? null;
    },
    enabled: !!params.beneficiary_id && !!params.scheduled_date,
  });

  useEffect(() => {
    if (existingQuery.data) {
      const log = existingQuery.data;
      setExistingLog(log);
      setStatus(log.status);
      setMinutesLate(log.minutes_late ? String(log.minutes_late) : '');
      setLateReason(log.late_reason as LateReason | null);
      setNote(log.note ?? '');
    }
  }, [existingQuery.data]);

  const setErrFromApi = (err: unknown) => {
    let message = 'Something went wrong. Please try again.';
    if (err instanceof ApiError && err.bodyText) {
      try {
        message = (JSON.parse(err.bodyText) as { detail?: string }).detail || message;
      } catch {
        /* ignore */
      }
    }
    setError(message);
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!status) throw new Error('Status required');
      return createExchangeLog(getToken, {
        beneficiary_id: Number(params.beneficiary_id),
        scheduled_date: params.scheduled_date,
        scheduled_time: params.scheduled_time || undefined,
        exchange_type: params.exchange_type as ExchangeType,
        status,
        minutes_late: status === 'LATE' ? Number(minutesLate) || undefined : undefined,
        note: note.trim() || undefined,
        late_reason: status === 'LATE' ? lateReason ?? undefined : undefined,
      });
    },
    onSuccess: () => {
      if (status) track('Exchange Logged', { status });
      void queryClient.invalidateQueries({ queryKey: ['exchange-logs'] });
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      navigate({ to: '/app/calendar' });
    },
    onError: setErrFromApi,
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!existingLog || !status) throw new Error('Missing data');
      return updateExchangeLog(getToken, existingLog.id, {
        status,
        minutes_late: status === 'LATE' ? Number(minutesLate) || undefined : undefined,
        note: note.trim() || undefined,
        late_reason: status === 'LATE' ? lateReason ?? undefined : undefined,
      });
    },
    onSuccess: () => {
      if (status) track('Exchange Log Updated', { status });
      void queryClient.invalidateQueries({ queryKey: ['exchange-logs'] });
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      navigate({ to: '/app/calendar' });
    },
    onError: setErrFromApi,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!existingLog) throw new Error('No log to delete');
      return deleteExchangeLog(getToken, existingLog.id);
    },
    onSuccess: () => {
      track('Exchange Log Deleted', { date: params.scheduled_date });
      void queryClient.invalidateQueries({ queryKey: ['exchange-logs'] });
      void queryClient.invalidateQueries({ queryKey: ['exchange-logs-all'] });
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      navigate({ to: '/app/calendar' });
    },
    onError: setErrFromApi,
  });

  const handleDelete = () => {
    if (
      !window.confirm(
        'Delete this exchange log? This action is recorded in the audit trail.'
      )
    ) {
      return;
    }
    deleteMutation.mutate();
  };

  const isPending =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const isEditing = !!existingLog;

  const canSave =
    status !== null &&
    (status !== 'LATE' || (minutesLate && Number(minutesLate) > 0));

  const doSave = () => {
    setError('');
    if (isEditing) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const handleSave = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59, 999);
    const scheduledDate = new Date(params.scheduled_date + 'T12:00:00');

    if (scheduledDate > tomorrow) {
      track('Future Exchange Alert Shown', { date: params.scheduled_date });
      if (
        !window.confirm(
          "This exchange hasn't happened yet. Are you sure you want to log it now?"
        )
      ) {
        return;
      }
      track('Future Exchange Confirmed', { date: params.scheduled_date });
    }

    doSave();
  };

  const isDropoff = params.exchange_type === 'DROPOFF';
  const coParentName = params.co_parent_name || 'Co-parent';
  const contextLine = isDropoff
    ? `Your drop-off of ${params.beneficiary_name}`
    : `${coParentName}'s drop-off to you`;
  const statusQuestion = isDropoff
    ? 'How did your drop-off go?'
    : `How was ${coParentName}'s drop-off?`;

  if (!params.beneficiary_id || !params.scheduled_date) {
    return (
      <div className='min-h-screen bg-sky-light p-6'>
        <p className='text-gray-600'>Missing exchange details.</p>
        <Link to='/app/calendar' className='text-primary font-semibold mt-4 inline-block'>
          Back to calendar
        </Link>
      </div>
    );
  }

  if (existingQuery.isLoading) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light pb-10'>
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg mx-auto px-6 py-4'>
          <Link
            to='/app/calendar'
            className='inline-flex items-center gap-2 text-primary hover:underline font-semibold'
          >
            <FiArrowLeft /> Back
          </Link>
        </div>
      </header>

      <div className='max-w-lg mx-auto px-6 py-6 space-y-6'>
        <Card className='space-y-3'>
          <h1 className='text-xl font-bold text-gray-900'>{params.beneficiary_name}</h1>
          <p className='text-sm text-gray-500'>{formatDisplayDate(params.scheduled_date)}</p>
          <p className='text-sm text-gray-700 flex items-center gap-2'>
            <span className='text-gray-400'>↔</span> {contextLine}
          </p>
          {params.scheduled_time ? (
            <p className='text-sm text-gray-600'>
              {formatTimeDisplay(params.scheduled_time)}
            </p>
          ) : null}
        </Card>

        <div>
          <p className='text-sm font-semibold text-gray-700 mb-3'>{statusQuestion}</p>
          <div className='grid grid-cols-2 gap-2'>
            {STATUS_OPTIONS.map((option) => {
              const selected = status === option.value;
              return (
                <button
                  key={option.value}
                  type='button'
                  onClick={() => {
                    track('Exchange Status Selected', { status: option.value });
                    setStatus(option.value);
                  }}
                  disabled={isPending}
                  className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-colors ${
                    selected ? '' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  style={
                    selected
                      ? {
                          borderColor: option.color,
                          backgroundColor: `${option.color}12`,
                        }
                      : undefined
                  }
                >
                  <option.Icon
                    className='w-7 h-7'
                    style={{ color: selected ? option.color : '#9CA3AF' }}
                  />
                  <span
                    className='text-sm font-semibold'
                    style={{ color: selected ? option.color : '#6B7280' }}
                  >
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {status === 'LATE' && (
          <Card className='space-y-4'>
            <div>
              <label className='block text-sm font-semibold text-gray-600 mb-2'>
                How many minutes late?
              </label>
              <input
                type='number'
                min={1}
                value={minutesLate}
                onChange={(e) => setMinutesLate(e.target.value)}
                placeholder='e.g. 15'
                className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50'
              />
            </div>
            <div>
              <p className='text-sm font-semibold text-gray-600 mb-2'>Reason (optional)</p>
              <div className='flex flex-wrap gap-2'>
                {LATE_REASONS.map((reason) => {
                  const selected = lateReason === reason.value;
                  return (
                    <button
                      key={reason.value}
                      type='button'
                      onClick={() =>
                        setLateReason(selected ? null : reason.value)
                      }
                      className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${
                        selected
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white border-gray-200 text-gray-700'
                      }`}
                    >
                      {reason.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        <div>
          <label className='block text-sm font-semibold text-gray-600 mb-2'>
            Note (optional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder='Add any details about this exchange…'
            className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y min-h-[88px]'
          />
        </div>

        {error ? <p className='text-sm text-red-600 text-center'>{error}</p> : null}

        <Button
          fullWidth
          onClick={handleSave}
          disabled={!canSave || isPending}
          loading={isPending}
        >
          {isEditing ? 'Save changes' : 'Save log'}
        </Button>

        {isEditing && existingLog ? (
          <div className='space-y-3'>
            <p className='text-center text-sm text-gray-500'>Current status</p>
            <div className='flex justify-center'>
              <ExchangeStatusBadge
                status={existingLog.status}
                minutesLate={existingLog.minutes_late}
              />
            </div>
            <Button variant='danger' fullWidth onClick={handleDelete} disabled={isPending}>
              Delete log
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/calendar/log-exchange')({
  component: LogExchangePage,
  validateSearch: (search: Record<string, unknown>) => ({
    beneficiary_id: String(search.beneficiary_id ?? ''),
    beneficiary_name: String(search.beneficiary_name ?? ''),
    scheduled_date: String(search.scheduled_date ?? ''),
    scheduled_time:
      typeof search.scheduled_time === 'string' ? search.scheduled_time : '',
    exchange_type: search.exchange_type === 'DROPOFF' ? 'DROPOFF' : 'PICKUP',
    co_parent_name:
      typeof search.co_parent_name === 'string' ? search.co_parent_name : '',
  }),
});
